import { singleton, inject } from "tsyringe";

// shared
import type { IDateAdapter } from "@/shared/libs/date/interface";
import type { HttpAdapter } from "@/shared/libs/http/interface";

import type {
    UserRepository,
    RequestPatchUserEnhancementSetting,
    RequestGetExpectedAttendeesPayload,
    RequestGetTeachersPayload,
    RequestAcademyStudentsPayload,
    RequestGetFormerStudentsPayload,
    RequestGetPrincipalStudentsPayload,
    RequestPatchStudentPayload,
    RequestPatchStudentStudySetting,
} from "@/entities/user/models/repository";
import type {
    GetTeachersResponseDto,
    GetExpectedAttendeesResponseDto,
    TeacherDto,
    GetAcademyStudentsResponseDto,
    GetFormerStudents,
    GetPrincipalStudentsDto,
    StudentDto,
    UserDto,
} from "@/entities/user/models/dtos";

@singleton()
export default class UserRepositoryImpl implements UserRepository {
    constructor(
        @inject("HttpAdapter") private readonly server: HttpAdapter,
        @inject("DateAdapter")
        private readonly dateAdapter: IDateAdapter,
    ) {}

    /**
     * @description 학생의 모든 정보를 가져옵니다.
     * @param studentId 학생 아이디
     * @returns
     */
    public getAcademyStudentAllInformation = async (studentId: string) => {
        const url = `/v2/students/${studentId}?include=timetable&include=curriculumMetadata&include=options`;

        const response = await this.server.get<StudentDto>(url);

        return response;
    };

    /**
     * @description 학생의 정보 중 튼튼 개념 쌓기에 대한 정보를 추가해서 가져옵니다.
     * @param studentId 학생아이디
     * @returns
     */
    public getStudentIncludeConceptEnhancementSetting = async (studentId: string) => {
        const url = `/v2/students/${studentId}?include=options`;

        const response = await this.server.get<StudentDto>(url);

        return response;
    };

    /**
     *  @description
     *  등원학생 정보를 가져옵니다.
     */
    public getExpectedAttendees = async (payload: RequestGetExpectedAttendeesPayload) => {
        let url = `/v1/academies/${payload.academyId}/expected-attendees?include=timetable&`;

        if (payload.date) {
            url += `date=${this.dateAdapter.formatQueryDate(payload.date)}&`;
        } else {
            url += `date=${this.dateAdapter.formatQueryDate(new Date())}&`;
        }

        if (payload.size) {
            url += `size=${payload.size}&`;
        } else {
            url += `size=8&`;
        }

        if (payload.nextToken) {
            url += `nextToken=${payload.nextToken}`;
        }

        const response = await this.server.get<GetExpectedAttendeesResponseDto>(url);

        return response;
    };

    /**
     *  @description 선생님 정보를 가져옵니다.
     */
    public getTeacher = async (teacherId: string) => {
        const response = await this.server.get<TeacherDto>(`/v1/teachers/${teacherId}`);

        return response;
    };

    /**
     *  @description 선생님 정보를 가져옵니다.
     */
    public getTeachers = async (payload: RequestGetTeachersPayload) => {
        let url = `/v1/academies/${payload.academyId}/teachers?`;

        if (payload.size) {
            url += `size=${payload.size}`;
        } else {
            url += `size=8`;
        }

        if (payload.nextToken) {
            url += `nextToken=${payload.nextToken}`;
        }

        const response = await this.server.get<GetTeachersResponseDto>(url);

        return response;
    };

    /**
     * @description 퇴원생을 검색합니다.
     *  */
    public getFormerStudents = async (payload: RequestGetFormerStudentsPayload) => {
        let url = `/v1/academies/${payload.academyId}/former-students?`;

        const params: string[] = [];

        if (payload.keyword) {
            params.push(`keyword=${payload.keyword}`);
        }

        if (payload.size) {
            params.push(`size=${payload.size}`);
        } else {
            params.push(`size=10`);
        }

        if (payload.nextToken) {
            params.push(`nextToken=${payload.nextToken}`);
        }

        url += params.join("&");

        const response = await this.server.get<GetFormerStudents>(url);

        return response;
    };

    /** @description 총 재원생을 가져옵니다. */
    public getPrincipalStudents = async (payload: RequestGetPrincipalStudentsPayload) => {
        let url = `/v2/academies/${payload.academyId}/students?from=${payload.from}&to=${payload.to}&`;

        if (payload.size) {
            url += `size=${payload.size}`;
        } else {
            url += `size=1`;
        }

        if (payload.nextToken) {
            url += `nextToken=${payload.nextToken}`;
        }

        const response = await this.server.get<GetPrincipalStudentsDto>(url);

        return response;
    };

    /** @description 학생들을 호출합니다. */
    public getAcademyStudents = async (payload: RequestAcademyStudentsPayload) => {
        let url = `/v1/academies/${payload.academyId}/students?include=class&`;

        const params: string[] = [];

        const order = encodeURIComponent(`[{"${payload.orders.type}":${payload.orders.order}}]`);
        params.push(`orders=${order}`);

        if (payload.size) {
            params.push(`size=${payload.size}`);
        } else {
            params.push(`size=10`);
        }

        if (payload.keyword) {
            params.push(`keyword=${payload.keyword}`);
        }

        if (payload.classId && payload.classId.length > 0) {
            for (const classId of payload.classId) {
                params.push(`classId=${classId}`);
            }
        }

        if (payload.nextToken) {
            params.push(`nextToken=${payload.nextToken}`);
        }

        url += params.join("&");

        const response = await this.server.getInBff<GetAcademyStudentsResponseDto>(url);

        return response;
    };

    /**
     * @description 학생의 정보를 수정합니다.
     */
    public patchStudent = async (payload: RequestPatchStudentPayload) => {
        const url = `/v4/students/${payload.studentId}`;

        const response = await this.server.patch<UserDto>(url, payload.requestDto);

        return response;
    };

    /**
     * @description 유저의 튼튼 개념 쌓기 설정을 수정합니다.
     * @param userId 유저 아이디
     * @param payload 수정할 설정 정보
     */
    public patchUserConceptEnhancementSetting = async (
        userId: string,
        payload: RequestPatchUserEnhancementSetting,
    ) => {
        const url = `/v1/users/${userId}`;

        await this.server.patch(url, payload);
    };

    /**
     * @description 유저 비밀번호를 초기화합니다.
     * backend: PATCH /v1/users/:userId (바디 없이 처리)
     */
    public resetPassword = async (userId: string) => {
        const url = `/v1/users/${userId}`;

        // backend은 request body를 사용하지 않지만, HttpAdapter.patch는 data 인자가 필요합니다.
        await this.server.patch<void>(url, {});
    };

    /**
     * @description 학생의 튼튼 개념 쌓기 설정을 수정합니다.
     * @param studentId 학생 아이디
     * @param payload 수정할 설정 정보
     */
    public patchStudentConceptEnhancementSetting = async (
        studentId: string,
        payload: RequestPatchUserEnhancementSetting,
    ) => {
        const url = `/v3/students/${studentId}?include=options`;

        await this.server.patch(url, payload);
    };

    /**
     * @description 학생의 학습 설정을 수정합니다.
     * @param {string} studentId 학생 아이디
     * @param {RequestPatchStudentStudySetting} payload 수정할 설정 정보
     */
    public patchStudentStudySetting = async (
        studentId: string,
        payload: RequestPatchStudentStudySetting,
    ) => {
        const url = `/v1/students/${studentId}`;

        await this.server.patchInBff(url, payload);
    };
}
